import type { QuickSearchApiClient, QuickSearchFilters } from '@/scopes/search/quick-search/domain/types'

import { createQueryStringSerializer } from '@/scopes/common/infrastructure/query-strings/queryStringSerializer'
import { createRoomsParticipantDistributionMapper } from '@/scopes/search/search/infrastructure/api/filtersSelectionMapper/mappers/roomsParticipantDistributionMapper'
import defaultFetch from 'isomorphic-unfetch'
import { createQuickSearchFiltersParser } from './quickSearchFiltersParser'
import quickSearchFilterResponseMapper from './quickSearchResponseMapper'

interface QuickSearchContext {
  contextItemId?: string
  publicationCode?: string
}

type Criteria = QuickSearchContext & {
  filterSelection?: QuickSearchFilters
}

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string
  }
  fetch: typeof defaultFetch
  roomsParticipantDistributionMapper: ReturnType<typeof createRoomsParticipantDistributionMapper>
  contextItemId: string
}

export function createQuickSearchApiClient(dependencies: Partial<Dependencies>): QuickSearchApiClient {
  const {
    fetch = defaultFetch,
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    roomsParticipantDistributionMapper = createRoomsParticipantDistributionMapper(),
  } = dependencies

  const queryStringSerializer = createQueryStringSerializer<QuickSearchFilters & QuickSearchContext>({
    parser: createQuickSearchFiltersParser({ roomsParticipantDistributionMapper }),
  })

  const fetchOptions: RequestInit = {
    referrerPolicy: 'no-referrer-when-downgrade',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
  }

  async function getQuickSearch(criteria: Criteria) {
    const url = buildAPIUrl(criteria)
    const response = await fetch(url, fetchOptions)

    const data = await response.json()

    return quickSearchFilterResponseMapper.map(data)
  }

  function buildAPIUrl(criteria: Criteria) {
    const { contextItemId, publicationCode, filterSelection } = criteria

    const queryString = queryStringSerializer.serialize({
      contextItemId,
      publicationCode,
      ...filterSelection,
    })

    return `${env.NEXT_PUBLIC_API_BASE_URL}/QuickSearch/GetQuickSearchApi?requiredFilters=Participants%2BAllocation%2BParticipantsDistribution%2BDepartureAirport%2BDestination%2BDepartureDate%2BTransportType%2BDuration&${queryString}`
  }

  return {
    getQuickSearch,
  }
}
